import { useState, useMemo, useEffect } from 'react';
import { Button, Box, Typography, Autocomplete, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import DataTable from '../../common/DataTable/index';
import Search from '../../common/Search';
import CommonDialog from '../../common/CommonDialog';
import AddArea from './AddArea';
import { fetchAreas } from '../../../../apis/Masters/Area';
import { fetchDcrAreas } from '../../../../apis/DCR/Area';
import ExportExcel from '../../common/ExportExcel';
import { useResponsive, isDcrScreen, userScreenAccess } from '../../../../utils/MasterUtils';
import { statusOption } from '../../../../constants/constant';
import { FilterFieldSx, DropDownSX } from '../../../../constants/style';

let _editRowHandler = null;

const headers = [
  {
    label: 'Area',
    key: 'area',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'City',
    key: 'city',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    sortable: true,
    align: 'center',
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const dcrHeaders = [
  {
    label: 'Area',
    key: 'dailyCallArea',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'City',
    key: 'dailyCallCity',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    sortable: true,
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const AreaList = () => {
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusGUID, setStatusGUID] = useState(1);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null); // null = Add mode
  const { isMobile } = useResponsive();

  const isDcrArea = isDcrScreen();
  const tableHeader = isDcrArea ? dcrHeaders : headers;

  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedArea(row);
    setOpenDialog(true);
  };

  const onRowClick = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedArea(row);
    setOpenDialog(true);
  };

  // Open Add dialog
  const handleAddArea = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedArea(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedArea(null);
  };

  const fetchAreaListResponse = async (status) => {
    if (status === undefined) status = statusGUID; // use current state if not provided
    setIsLoading(true);
    try {
      const response = isDcrArea ? await fetchDcrAreas(status) : await fetchAreas(status);
      setAreas(response || []);
    } catch (err) {
      console.log('Error fetching area list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (event, value) => {
    const status = value?.value;
    setStatusGUID(status);
    fetchAreaListResponse(status);
  };

  useEffect(() => {
    fetchAreaListResponse(statusGUID);
  }, [isDcrArea]);

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return areas;
    return areas.filter((row) => {
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, areas]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Area List
        </Typography>
      </Box>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <Autocomplete
          size="small"
          options={statusOption}
          value={statusOption.find((u) => u.value === statusGUID) || null}
          onChange={handleStatusChange}
          disableClearable={true}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          sx={{
            width: '30%'
          }} // adjust width here
          slotProps={{
            popper: {
              sx: DropDownSX
            }
          }}
          renderInput={(params) => (
            <TextField {...params} sx={FilterFieldSx} placeholder="Select Status" />
          )}
        />
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by area, city or status..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={areas}
            fileName={`${isDcrArea ? 'DailyCallArea' : 'Area'}_${new Date()
              .toISOString()
              .slice(0, 10)}.xlsx`}
            sheetName={isDcrArea ? 'DailyCallArea' : 'Area'}
            buttonLabel="Excel"
            exportKeys={tableHeader.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={tableHeader.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddArea}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={tableHeader}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}"` : 'No areas found in the system.'
        }
        enablePagination={true}
        zebra={false}
        onRowClick={onRowClick}
      />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={
          selectedArea
            ? `Edit Area — ${selectedArea.area || selectedArea.dailyCallArea}`
            : 'Add New Area'
        }
        maxWidth="sm"
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddArea
          isDcrArea={isDcrArea}
          areaData={selectedArea}
          userAccess={userAccess}
          onRefresh={fetchAreaListResponse}
          onClose={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default AreaList;
